/**
 * Workspace Settings → General. Rename only for v1.
 */
import { useState, type FormEvent } from 'react'
import { Button, Input, Label } from '@/components/ui'
import type { WorkspaceSummary } from '@/lib/api'
import { workspacesAPI } from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { useCanIWorkspace } from '@/hooks/useCanI'

export function WorkspaceGeneral({ workspace }: { workspace: WorkspaceSummary }) {
  const canEdit = useCanIWorkspace('workspaces.update', workspace._id)
  const [name, setName] = useState(workspace.name)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Name is required.')
      return
    }
    if (trimmed === workspace.name) {
      setSavedAt(Date.now())
      return
    }
    setSubmitting(true)
    try {
      await workspacesAPI.update(workspace._id, { name: trimmed })
      // The AppContextProvider doesn't auto-refresh on workspace
      // mutations; reload to pick up the new name in the picker
      // breadcrumb. Heavier than React-Query-style mutation cache
      // invalidation, but the existing pattern is full-page
      // refreshes for cross-cutting state changes.
      window.location.reload()
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't save the workspace settings. Try again — if the problem continues, check your connection.",
        ),
      )
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
      <div className="space-y-2">
        <Label htmlFor="workspace-name">Workspace name</Label>
        <Input
          id="workspace-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canEdit || submitting}
          required
        />
        {!canEdit && (
          <p className="text-xs text-muted-foreground">
            Only owners and admins can rename this workspace.
          </p>
        )}
      </div>

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {savedAt && !error && (
        <p className="text-sm text-success" key={savedAt}>Saved.</p>
      )}

      {canEdit && (
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting || name.trim() === workspace.name}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={submitting || name === workspace.name}
            onClick={() => setName(workspace.name)}
          >
            Reset
          </Button>
        </div>
      )}
    </form>
  )
}
